import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useSearchParams} from '@github-ui/use-navigate'
import {Button, Heading} from '@primer/react'
import {useState} from 'react'

import type {LabelArchiveType} from './constants/values'
import {LabelCreate} from './LabelCreate'
import {LabelList} from './LabelList'
import {SearchBar} from './LabelSearchBar'
import type {RepositoryNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'
import {parseArchivedParam} from './utils/archived-param'

export type RepositoryLabelsInternalFragment = Pick<
  RepositoryNode,
  'id' | 'nameWithOwner' | 'labels' | 'isWritable' | 'viewerCanPush'
>

type LabelsHeaderProps = {
  sidebarLayoutEnabled: boolean
  isControlled: boolean
  viewerCanPush: boolean
  onCreateDialogOpen: () => void
}

function LabelsHeader({sidebarLayoutEnabled, isControlled, viewerCanPush, onCreateDialogOpen}: LabelsHeaderProps) {
  return (
    <>
      <div className={styles.header}>
        {!sidebarLayoutEnabled && (
          <div className={styles.titleGroup}>
            <SidebarPageLayout.SidebarTrigger />
            <Heading as="h1" className={styles.heading}>
              Labels
            </Heading>
          </div>
        )}
        {!isControlled && viewerCanPush && (
          <Button variant="primary" onClick={onCreateDialogOpen}>
            New label
          </Button>
        )}
      </div>
      <SearchBar />
    </>
  )
}

type RepositoryLabelsInternalProps = {
  repository: RepositoryLabelsInternalFragment
  queryKey?: RouteQueryKey
  isCreateDialogOpen?: boolean
  onCreateDialogOpenChange?: (open: boolean) => void
}

export function RepositoryLabelsInternal({
  repository,
  queryKey,
  isCreateDialogOpen: controlledOpen,
  onCreateDialogOpenChange,
}: RepositoryLabelsInternalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = onCreateDialogOpenChange !== undefined
  const isCreateDialogOpen = isControlled ? !!controlledOpen : uncontrolledOpen
  const setIsCreateDialogOpen = isControlled ? onCreateDialogOpenChange : setUncontrolledOpen

  const data = repository

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const [searchParams] = useSearchParams()

  let archiveType: LabelArchiveType | undefined = undefined
  const isArchivingEnabled = isFeatureEnabled('labels_archiving')

  if (isArchivingEnabled) {
    archiveType = parseArchivedParam(searchParams.get('archived'))
  }

  return (
    <>
      <div className={sidebarLayoutEnabled ? undefined : styles.middlePaneWrapper}>
        <LabelsHeader
          sidebarLayoutEnabled={sidebarLayoutEnabled}
          isControlled={isControlled}
          viewerCanPush={data.viewerCanPush}
          onCreateDialogOpen={() => setIsCreateDialogOpen(true)}
        />
        <ErrorBoundary boundaryName="LabelsPage" fallback={<div>Oops we could not load the labels</div>}>
          <LabelList
            repository={data}
            onCreateLabel={() => setIsCreateDialogOpen(true)}
            queryKey={queryKey}
            archiveType={archiveType}
          />
        </ErrorBoundary>
      </div>
      <LabelCreate
        repository={data}
        isOpen={isCreateDialogOpen}
        setDialogOpen={setIsCreateDialogOpen}
        queryKey={queryKey}
      />
    </>
  )
}
